import { useContext, useMemo } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';

// Channel ids the purchase-history service expects. Mirrors recent-order-details'
// own getChannelId mapping, which isn't exported from that package.
const CHANNEL_IDS = {
  desktop: '1',
  mobile: '2',
  tablet: '3',
  prox: '4',
  consumerapp: '5',
  proapp: '6',
};

// The purchase-history call looks back 25 months, matching the RecentPurchases card.
const MONTHS_OF_HISTORY = 25;

const yyyymmdd = (date) => date.toISOString().slice(0, 10);

/**
 * Subtracts `months` from `date`, clamping to the last day of the target month rather
 * than overflowing into the month after it. `Date#setMonth` alone retains the current
 * day-of-month, so e.g. subtracting 1 month from Mar 31 lands on Mar 3 (Feb only has 28
 * days) instead of Feb 28.
 */
const subtractMonthsClamped = (date, months) => {
  const day = date.getDate();
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() - months);
  const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDayOfTargetMonth));
  return result;
};

/**
 * Fetches the customer's recent orders for the "Your Latest Orders" tab, using the same
 * federated `purchaseHistory` query that backs the RecentPurchases card. Each order is
 * rendered by a RecentPurchasesTile, which fetches its own per-order detail.
 *
 * `customerInfo` is derived here rather than per tile so every tile shares one identity
 * (and the query and the tiles agree on tmxProfileId/channelId). tmxProfileId comes from
 * the account library's ThreatMetrix session, which only exists in the browser. Memoized
 * so a stable `orders` reference downstream doesn't get skipped past when only the
 * identity/channel changes.
 *
 * Returns { orders, count, loading, error, customerInfo }.
 */
export const useLatestOrders = (userID, svocID) => {
  const { channel } = useContext(ExperienceContext) || {};
  const channelId = CHANNEL_IDS[channel?.toLowerCase()] || CHANNEL_IDS.desktop;
  const sessionId = (typeof window !== 'undefined' && window.THD_ACCOUNT_LIB?.TMX?.sessionId) || '';

  const endDate = new Date();
  const startDate = subtractMonthsClamped(endDate, MONTHS_OF_HISTORY);

  const { data, loading, error } = useDataModel('purchaseHistory', {
    variables: {
      userId: userID,
      request: {
        searchType: 'ORDERS',
        pageSize: 20,
        pageNumber: 1,
        resultsFilter: 'myOrders',
        sortBy: 'salesDate',
        sortOrder: 'DESC',
        customerAccountId: svocID,
        startDate: yyyymmdd(startDate),
        endDate: yyyymmdd(endDate),
      },
      headers: { tmxProfileId: sessionId, channelId },
    },
    skip: !userID || !svocID,
    ssr: false,
    context: { withAuth: true },
  });

  const customerInfo = useMemo(() => ({
    userId: userID,
    customerAccountId: svocID,
    sessionId,
    channelId,
  }), [userID, svocID, sessionId, channelId]);

  return {
    orders: data?.purchaseHistory?.orders || [],
    count: data?.purchaseHistory?.orderCount,
    loading,
    error,
    customerInfo,
  };
};
